import { useMemo, useState, useEffect, useCallback } from 'react'
import { useParams, useNavigate } from '@tanstack/react-router'
import { useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { Button } from './ui/button'
import { Badge } from './ui/badge'
import { Input } from './ui/input'
import { Label } from './ui/label'
import { Checkbox } from './ui/checkbox'
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs'
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from './ui/alert-dialog'
import { mdiCheck, mdiClose, mdiContentSave, mdiFileTree, mdiFlaskOutline, mdiLoading, mdiOpenInNew, mdiPound, mdiTrashCanOutline } from '@mdi/js'
import { AppIcon } from './ui/icon'
import { Link } from '@tanstack/react-router'
import { useBreadcrumbs } from '../hooks/useBreadcrumbs'
import { useRouteTab } from '../hooks/useRouteTab'
import { $api } from '../api/queryClient'
import { useCan } from '../contexts/PermissionsContext'
import { PipelineEditor } from './normalizations/PipelineEditor'
import { NORMALIZATION_TABS } from './normalizations/tabs'
import { NormalizationTestPanel } from './normalizations/NormalizationTestPanel'
import type { NormalizationOp } from './normalizations/OperationEditDialog'

function invalidateNormalization(queryClient: ReturnType<typeof useQueryClient>) {
  queryClient.invalidateQueries({
    predicate: (query) => {
      const queryKey = query.queryKey as any[]
      if (queryKey.length >= 2 && queryKey[0] === 'get') {
        const path = queryKey[1] as string
        return path.includes('/normalizations')
      }
      return false
    }
  })
}

export function NormalizationDetailPage() {
  const { org, uid } = useParams({ strict: false }) as { org: string; uid: string }
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { t } = useTranslation('derivations')
  const [showDeleteDialog, setShowDeleteDialog] = useState(false)
  // The tab lives in the URL (`?tab=pipeline`), so it can be linked and survives a reload.
  const [activeTab, setActiveTab] = useRouteTab(NORMALIZATION_TABS, 'overview')

  // Editable fields
  const [enabled, setEnabled] = useState(true)
  const [pipeline, setPipeline] = useState<NormalizationOp[]>([])
  const [pipelineDirty, setPipelineDirty] = useState(false)
  const [editingName, setEditingName] = useState(false)
  // Normalizations belong to the type system — `configuration.write` gates every
  // mutation; the values themselves stay readable (spec 2026-09-04-02, "Gating").
  const canWriteConfiguration = useCan('configuration.write')
  const [nameValue, setNameValue] = useState('')

  const { data: normalization, isLoading, error } = $api.useQuery('get', '/api/v1/organizations/{orgCode}/normalizations/{normalizationUid}' as any, {
    params: { path: { orgCode: org, normalizationUid: uid } }
  })

  const n = normalization as any

  useBreadcrumbs(useMemo(() => [
    { title: t('common:nav.home'), path: `/${org}` },
    { title: t('common:nav.settings'), path: `/${org}/settings` },
    { title: t('normalizations.breadcrumb'), path: `/${org}/normalizations` },
    { title: n?.name || n?.property?.name?.en || n?.property?.code || t('normalizations.detail.loading') }
  ], [org, n, t]))

  useEffect(() => {
    if (n) {
      setEnabled(n.enabled ?? true)
      setNameValue(n.name || '')
      try {
        const parsed = typeof n.pipeline === 'string' ? JSON.parse(n.pipeline) : n.pipeline
        setPipeline(Array.isArray(parsed) ? parsed : [])
      } catch {
        setPipeline([])
      }
      setPipelineDirty(false)
    }
  }, [n])

  const updateMutation = $api.useMutation('patch', '/api/v1/organizations/{orgCode}/normalizations/{normalizationUid}' as any, {
    onSuccess: () => {
      invalidateNormalization(queryClient)
      setPipelineDirty(false)
    }
  })

  const deleteMutation = $api.useMutation('delete', '/api/v1/organizations/{orgCode}/normalizations/{normalizationUid}' as any, {
    onSuccess: () => {
      invalidateNormalization(queryClient)
      navigate({ to: '/$org/normalizations', params: { org } })
    }
  })

  const handleToggleEnabled = useCallback((checked: boolean) => {
    setEnabled(checked)
    updateMutation.mutate({
      params: { path: { orgCode: org, normalizationUid: uid } },
      body: { enabled: checked },
    } as any)
  }, [org, uid, updateMutation])

  const handleSavePipeline = useCallback(() => {
    updateMutation.mutate({
      params: { path: { orgCode: org, normalizationUid: uid } },
      body: { pipeline },
    } as any)
  }, [org, uid, pipeline, updateMutation])

  const handlePipelineChange = useCallback((newPipeline: NormalizationOp[]) => {
    setPipeline(newPipeline)
    setPipelineDirty(true)
  }, [])

  const handleSaveName = useCallback(() => {
    updateMutation.mutate({
      params: { path: { orgCode: org, normalizationUid: uid } },
      body: { name: nameValue },
    } as any)
    setEditingName(false)
  }, [org, uid, nameValue, updateMutation])

  const handleDelete = useCallback(() => {
    deleteMutation.mutate({
      params: { path: { orgCode: org, normalizationUid: uid } },
    } as any)
  }, [org, uid, deleteMutation])

  if (isLoading) {
    return (
      <div className="flex justify-center py-16">
        <AppIcon path={mdiLoading} size="xl" className="motion-safe:animate-spin" />
      </div>
    )
  }

  if (error || !n) {
    return (
      <div className="container mx-auto py-8 max-w-4xl">
        <div className="text-destructive text-center py-8">
          {t('normalizations.not_found')}
        </div>
      </div>
    )
  }

  return (
    <div className="container mx-auto py-8 max-w-4xl">
      {/* Header */}
      <div className="flex items-start justify-between mb-6">
        <div>
          {editingName && canWriteConfiguration ? (
            <div className="flex items-center gap-2">
              <Input
                value={nameValue}
                onChange={(e) => setNameValue(e.target.value)}
                className="text-xl font-bold h-9"
                autoFocus
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleSaveName()
                  if (e.key === 'Escape') { setEditingName(false); setNameValue(n.name || '') }
                }}
                data-testid="normalization-name-input"
              />
              <Button size="icon" variant="ghost" onClick={handleSaveName} data-testid="normalization-name-save">
                <AppIcon path={mdiCheck} size="sm" />
              </Button>
              <Button size="icon" variant="ghost" onClick={() => { setEditingName(false); setNameValue(n.name || '') }} data-testid="normalization-name-cancel">
                <AppIcon path={mdiClose} size="sm" />
              </Button>
            </div>
          ) : (
            <h1
              className={`text-2xl font-bold text-foreground${canWriteConfiguration ? ' cursor-pointer hover:text-primary/80' : ''}`}
              onClick={canWriteConfiguration ? () => setEditingName(true) : undefined}
              data-testid="normalization-name-display"
            >
              {n.name || n.property?.name?.en || n.property?.code || 'Normalization'}
            </h1>
          )}
          {n.property?.code && (
            <code className="text-sm text-muted-foreground">{n.property.code}</code>
          )}
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            {/* The toggle doubles as the enabled indicator: it stays on screen,
                read-only, rather than disappearing. */}
            <Checkbox
              checked={enabled}
              disabled={!canWriteConfiguration}
              onCheckedChange={(checked) => handleToggleEnabled(checked === true)}
              data-testid="normalization-enabled-toggle"
            />
            <Label className="text-sm">{t('normalizations.detail.enabled_label')}</Label>
          </div>
          {canWriteConfiguration && (
            <Button
              variant="destructive"
              size="icon"
              onClick={() => setShowDeleteDialog(true)}
              data-testid="delete-normalization-btn"
            >
              <AppIcon path={mdiTrashCanOutline} size="sm" />
            </Button>
          )}
        </div>
      </div>

      {/* Summary badges */}
      <div className="flex gap-2 mb-6 ml-10">
        <Link to="/$org/properties/$propertyUid" params={{ org, propertyUid: n.property?.uid }} data-testid="property-badge-link">
          <Badge variant="secondary" className="gap-1.5 cursor-pointer hover:bg-accent">
            {t('normalizations.detail.property_badge')} <code className="text-xs">{n.property?.code}</code>
            <AppIcon path={mdiOpenInNew} size="xs" />
          </Badge>
        </Link>
        <Badge variant="secondary" className="gap-1.5">
          {t('normalizations.badges.operations_count', { count: pipeline.length })}
        </Badge>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="mb-6">
          <TabsTrigger value="overview" data-testid="tab-overview" className="gap-1.5">
            <AppIcon path={mdiPound} size="xs" />
            {t('normalizations.detail.tabs.overview')}
          </TabsTrigger>
          <TabsTrigger value="pipeline" data-testid="tab-pipeline" className="gap-1.5">
            <AppIcon path={mdiFileTree} size="xs" />
            {t('normalizations.detail.tabs.pipeline')}
          </TabsTrigger>
          <TabsTrigger value="test" data-testid="tab-test" className="gap-1.5">
            <AppIcon path={mdiFlaskOutline} size="xs" />
            {t('normalizations.detail.tabs.test')}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="overview">
          <div className="space-y-6">
            {/* Property Info */}
            <div className="bg-card rounded-lg border border-border p-5">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">{t('normalizations.detail.property_section')}</h3>
                {n.property?.uid && (
                  <Link
                    to="/$org/properties/$propertyUid"
                    params={{ org, propertyUid: n.property.uid }}
                    className="text-sm text-primary hover:underline flex items-center gap-1"
                    data-testid="property-detail-link"
                  >
                    {t('normalizations.detail.view_property')} <AppIcon path={mdiOpenInNew} size="xs" />
                  </Link>
                )}
              </div>
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <span className="text-sm text-muted-foreground w-20">{t('normalizations.detail.property_name_label')}</span>
                  <span className="text-sm font-medium">{n.property?.name?.en || '-'}</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-muted-foreground w-20">{t('normalizations.detail.property_code_label')}</span>
                  <code className="text-sm bg-muted px-1.5 py-0.5 rounded">{n.property?.code || '-'}</code>
                </div>
                {n.name && (
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-muted-foreground w-20">{t('normalizations.detail.property_label_label')}</span>
                    <span className="text-sm font-medium">{n.name}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Pipeline Summary */}
            <div className="bg-card rounded-lg border border-border p-5">
              <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-4">{t('normalizations.detail.pipeline_section')}</h3>
              <code className="text-sm text-muted-foreground">
                {pipeline.map(op => {
                  if (op.op === 'map') return `map(${Object.keys(op.values || {}).length} values)`
                  return op.op
                }).join(' → ') || t('normalizations.detail.empty_pipeline')}
              </code>
            </div>

            {/* Meta */}
            <div className="text-xs text-muted-foreground space-x-3">
              <span>UID: {n.uid}</span>
              <span>Created: {new Date(n.created_at).toLocaleDateString()}</span>
              <span>Updated: {new Date(n.updated_at).toLocaleDateString()}</span>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="pipeline">
          <div className="space-y-4">
            <PipelineEditor
              pipeline={pipeline}
              onPipelineChange={handlePipelineChange}
              isPending={updateMutation.isPending}
            />
            {pipelineDirty && canWriteConfiguration && (
              <div className="flex justify-end">
                <Button
                  onClick={handleSavePipeline}
                  disabled={updateMutation.isPending}
                  data-testid="save-pipeline-btn"
                >
                  {updateMutation.isPending ? <AppIcon path={mdiLoading} size="sm" className="mr-2 motion-safe:animate-spin" /> : <AppIcon path={mdiContentSave} size="sm" className="mr-2" />}
                  {t('normalizations.detail.save_pipeline')}
                </Button>
              </div>
            )}
          </div>
        </TabsContent>

        <TabsContent value="test">
          <NormalizationTestPanel
            org={org}
            uid={uid}
            testValues={n.test_values || []}
            onTestValuesChange={(newTestValues) => {
              updateMutation.mutate({
                params: { path: { orgCode: org, normalizationUid: uid } },
                body: { test_values: newTestValues },
              } as any)
            }}
          />
        </TabsContent>
      </Tabs>

      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('normalizations.detail.delete_dialog.title')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('normalizations.detail.delete_dialog.description', { name: n.property?.name?.en || n.property?.code })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-delete">{t('normalizations.detail.delete_dialog.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="confirm-delete"
            >
              {t('normalizations.detail.delete_dialog.confirm')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
